import React, { useState, useEffect } from 'react';
import { Calendar, Filter, Download, TrendingUp, FileText, Award, Users, BarChart3 } from 'lucide-react';
import useInstructorReportAnalyticsStore from '@/stores/InstructorStore/InstructorReportAnalyticsStore';
import './ReportAnalytics.css';

const ReportsAnalytics: React.FC = () => {
  const {
    leaderboardData,
    reportCards,
    departmentPerformance,
    skillsAssessment,
    completionTrends,
    certificationDistribution,
    fetchLeaderboardData,
    fetchReportCards,
    fetchDepartmentPerformance,
    fetchSkillsAssessment,
    fetchCompletionTrends,
    fetchCertificationDistribution,
  } = useInstructorReportAnalyticsStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState('');
  const [selectedFormats, setSelectedFormats] = useState<string[]>(['PDF']);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [showBasicConfig, setShowBasicConfig] = useState(true);
  const [showFiltersSegments, setShowFiltersSegments] = useState(false);
  const [showReportSections, setShowReportSections] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [selectedSections, setSelectedSections] = useState<string[]>([
    'User Activity Overview',
    'Engagement Metrics',
    'Login Statistics',
    'Time Spent Analysis',
    'Most Active Users'
  ]);

  // Fetch data on component mount
  useEffect(() => {
    if (!leaderboardData) fetchLeaderboardData();
    if (!reportCards) fetchReportCards();
    if (!departmentPerformance) fetchDepartmentPerformance();
    if (!skillsAssessment) fetchSkillsAssessment();
    if (!completionTrends) fetchCompletionTrends();
    if (!certificationDistribution) fetchCertificationDistribution();
  }, [
    leaderboardData,
    reportCards,
    departmentPerformance,
    skillsAssessment,
    completionTrends,
    certificationDistribution,
    fetchLeaderboardData,
    fetchReportCards,
    fetchDepartmentPerformance,
    fetchSkillsAssessment,
    fetchCompletionTrends,
    fetchCertificationDistribution,
  ]);

  const handleGenerateReport = (reportName: string) => {
    setSelectedReport(reportName);
    setShowModal(true);
  };

  const handleFormatToggle = (format: string) => {
    if (selectedFormats.includes(format)) {
      setSelectedFormats(selectedFormats.filter(f => f !== format));
    } else {
      setSelectedFormats([...selectedFormats, format]);
    }
  };

  const handleDepartmentToggle = (dept: string) => {
    if (selectedDepartments.includes(dept)) {
      setSelectedDepartments(selectedDepartments.filter(d => d !== dept));
    } else {
      setSelectedDepartments([...selectedDepartments, dept]);
    }
  };

  const handleSectionToggle = (section: string) => {
    if (selectedSections.includes(section)) {
      setSelectedSections(selectedSections.filter(s => s !== section));
    } else {
      setSelectedSections([...selectedSections, section]);
    }
  };

  return (
    <div className="trainer-reports-container">
      <div className="trainer-reports-header">
        <div className="trainer-reports-header-top">
          <div className="trainer-reports-title-section">
            <h1>Reports & Analytics</h1>
            <p>Comprehensive insights into learning performance and progress</p>
          </div>
          <div className="trainer-reports-actions">
            <button className="trainer-reports-btn">
              <Calendar size={18} />
              Date Range
            </button>
            <button className="trainer-reports-btn">
              <Filter size={18} />
              Filters
            </button>
            <button className="trainer-reports-btn primary">
              <Download size={18} />
              Export All
            </button>
          </div>
        </div>
      </div>

      <div className="trainer-reports-cards">
        <div className="trainer-report-card">
          <div className="trainer-report-card-header">
            <div className="trainer-report-icon blue">
              <TrendingUp size={24} />
            </div>
            <div className="trainer-report-info">
              <h3>Learning Engagement Report</h3>
              <p>Detailed analysis of user engagement and activity</p>
            </div>
          </div>
          <div className="trainer-report-meta">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span>Last generated 2 hours ago</span>
          </div>
          <div className="trainer-report-formats">
            <span className="trainer-format-badge pdf">📄 PDF</span>
            <span className="trainer-format-badge excel">📊 EXCEL</span>
          </div>
          <button className="trainer-generate-btn">
            <Download size={18} />
            Generate Report
          </button>
        </div>

        <div className="trainer-report-card">
          <div className="trainer-report-card-header">
            <div className="trainer-report-icon green">
              <Award size={24} />
            </div>
            <div className="trainer-report-info">
              <h3>Course Completion Report</h3>
              <p>Track completion rates across all courses</p>
            </div>
          </div>
          <div className="trainer-report-meta">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span>Last generated 1 day ago</span>
          </div>
          <div className="trainer-report-formats">
            <span className="trainer-format-badge pdf">📄 PDF</span>
            <span className="trainer-format-badge excel">📊 EXCEL</span>
          </div>
          <button className="trainer-generate-btn" onClick={() => handleGenerateReport('Course Completion Report')}>
            <Download size={18} />
            Generate Report
          </button>
        </div>

        <div className="trainer-report-card">
          <div className="trainer-report-card-header">
            <div className="trainer-report-icon purple">
              <BarChart3 size={24} />
            </div>
            <div className="trainer-report-info">
              <h3>Skills Assessment Report</h3>
              <p>Analyze skill development and competency levels</p>
            </div>
          </div>
          <div className="trainer-report-meta">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span>Last generated 3 days ago</span>
          </div>
          <div className="trainer-report-formats">
            <span className="trainer-format-badge pdf">📄 PDF</span>
            <span className="trainer-format-badge excel">📊 EXCEL</span>
          </div>
          <button className="trainer-generate-btn" onClick={() => handleGenerateReport('Skills Assessment Report')}>
            <Download size={18} />
            Generate Report
          </button>
        </div>

        <div className="trainer-report-card">
          <div className="trainer-report-card-header">
            <div className="trainer-report-icon orange">
              <Users size={24} />
            </div>
            <div className="trainer-report-info">
              <h3>User Report</h3>
              <p>Overview of issued certifications and achievements</p>
            </div>
          </div>
          <div className="trainer-report-meta">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span>Last generated 1 week ago</span>
          </div>
          <div className="trainer-report-formats">
            <span className="trainer-format-badge pdf">📄 PDF</span>
            <span className="trainer-format-badge excel">📊 EXCEL</span>
          </div>
          <button className="trainer-generate-btn" onClick={() => handleGenerateReport('User Report')}>
            <Download size={18} />
            Generate Report
          </button>
        </div>
      </div>

      <div className="trainer-charts-grid">
        <div className="trainer-chart-card">
          <div className="trainer-chart-header">
            <div className="trainer-chart-title">
              <h3>Department Performance</h3>
              <p>Completion rates by department</p>
            </div>
            <button className="trainer-chart-expand">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 3 21 3 21 9"/>
                <polyline points="9 21 3 21 3 15"/>
                <line x1="21" y1="3" x2="14" y2="10"/>
                <line x1="3" y1="21" x2="10" y2="14"/>
              </svg>
            </button>
          </div>
          <div className="trainer-bar-chart">
            {departmentPerformance?.map((dept) => (
              <div key={dept.department} className="trainer-bar-group">
                <div className="trainer-bars">
                  <div className="trainer-bar completed" style={{ height: `${dept.completedPercentage}%` }}></div>
                  <div className="trainer-bar total" style={{ height: `${dept.totalPercentage}%` }}></div>
                </div>
                <div className="trainer-bar-label">{dept.department}</div>
              </div>
            ))}
          </div>
          <div className="trainer-chart-legend">
            <div className="trainer-legend-item">
              <div className="trainer-legend-dot green"></div>
              <span>Completed</span>
            </div>
            <div className="trainer-legend-item">
              <div className="trainer-legend-dot blue"></div>
              <span>Total Users</span>
            </div>
          </div>
        </div>

        <div className="trainer-chart-card">
          <div className="trainer-chart-header">
            <div className="trainer-chart-title">
              <h3>Skills Assessment</h3>
              <p>Average skill scores across organization</p>
            </div>
            <button className="trainer-chart-expand">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 3 21 3 21 9"/>
                <polyline points="9 21 3 21 3 15"/>
                <line x1="21" y1="3" x2="14" y2="10"/>
                <line x1="3" y1="21" x2="10" y2="14"/>
              </svg>
            </button>
          </div>
          <div className="trainer-radar-chart">
            <svg className="trainer-radar-svg" viewBox="0 0 300 300">
              <defs>
                <linearGradient id="radarGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#fbbf24', stopOpacity: 0.8 }} />
                  <stop offset="100%" style={{ stopColor: '#f59e0b', stopOpacity: 0.3 }} />
                </linearGradient>
              </defs>
              {/* Grid */}
              <polygon points="150,50 220,100 220,200 150,250 80,200 80,100" fill="none" stroke="#e5e7eb" strokeWidth="1"/>
              <polygon points="150,80 200,115 200,185 150,220 100,185 100,115" fill="none" stroke="#e5e7eb" strokeWidth="1"/>
              <polygon points="150,110 180,130 180,170 150,190 120,170 120,130" fill="none" stroke="#e5e7eb" strokeWidth="1"/>
              {/* Lines */}
              <line x1="150" y1="150" x2="150" y2="50" stroke="#e5e7eb" strokeWidth="1"/>
              <line x1="150" y1="150" x2="220" y2="100" stroke="#e5e7eb" strokeWidth="1"/>
              <line x1="150" y1="150" x2="220" y2="200" stroke="#e5e7eb" strokeWidth="1"/>
              <line x1="150" y1="150" x2="150" y2="250" stroke="#e5e7eb" strokeWidth="1"/>
              <line x1="150" y1="150" x2="80" y2="200" stroke="#e5e7eb" strokeWidth="1"/>
              <line x1="150" y1="150" x2="80" y2="100" stroke="#e5e7eb" strokeWidth="1"/>
              {/* Data polygon */}
              {skillsAssessment && skillsAssessment.length === 6 && (
                <polygon
                  points={`
                    150,${150 - (skillsAssessment[0].score * 1)}
                    ${150 + (skillsAssessment[1].score * 0.7)},${150 - (skillsAssessment[1].score * 0.5)}
                    ${150 + (skillsAssessment[2].score * 0.7)},${150 + (skillsAssessment[2].score * 0.5)}
                    150,${150 + (skillsAssessment[3].score * 1)}
                    ${150 - (skillsAssessment[4].score * 0.7)},${150 + (skillsAssessment[4].score * 0.5)}
                    ${150 - (skillsAssessment[5].score * 0.7)},${150 - (skillsAssessment[5].score * 0.5)}
                  `}
                  fill="url(#radarGradient)"
                  stroke="#f59e0b"
                  strokeWidth="2"
                />
              )}
              {/* Labels */}
              {skillsAssessment?.map((skill, index) => {
                const positions: Array<{ x: number; y: number; anchor: 'start' | 'middle' | 'end' }> = [
                  { x: 150, y: 40, anchor: 'middle' },      // Leadership (top)
                  { x: 235, y: 105, anchor: 'start' },      // Technical (top-right)
                  { x: 235, y: 205, anchor: 'start' },      // Communication (bottom-right)
                  { x: 150, y: 275, anchor: 'middle' },     // Problem Solving (bottom)
                  { x: 60, y: 205, anchor: 'end' },         // Teamwork (bottom-left)
                  { x: 60, y: 105, anchor: 'end' },         // Time Management (top-left)
                ];
                const pos = positions[index];
                return pos ? (
                  <text key={skill.skill} x={pos.x} y={pos.y} textAnchor={pos.anchor} fontSize="11" fill="#6b7280">
                    {skill.skill}
                  </text>
                ) : null;
              })}
            </svg>
          </div>
        </div>
      </div>

      <div className="trainer-charts-grid">
        <div className="trainer-chart-card">
          <div className="trainer-chart-header">
            <div className="trainer-chart-title">
              <h3>Completion Trends</h3>
              <p>Monthly completion vs enrollment</p>
            </div>
            <button className="trainer-chart-expand">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 3 21 3 21 9"/>
                <polyline points="9 21 3 21 3 15"/>
                <line x1="21" y1="3" x2="14" y2="10"/>
                <line x1="3" y1="21" x2="10" y2="14"/>
              </svg>
            </button>
          </div>
          <div className="trainer-area-chart">
            <svg className="trainer-area-svg" viewBox="0 0 400 220">
              <defs>
                <linearGradient id="completedGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#10b981', stopOpacity: 0.6 }} />
                  <stop offset="100%" style={{ stopColor: '#10b981', stopOpacity: 0.05 }} />
                </linearGradient>
                <linearGradient id="enrolledGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#3b82f6', stopOpacity: 0.4 }} />
                  <stop offset="100%" style={{ stopColor: '#3b82f6', stopOpacity: 0.05 }} />
                </linearGradient>
                <linearGradient id="targetGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#f59e0b', stopOpacity: 0.3 }} />
                  <stop offset="100%" style={{ stopColor: '#f59e0b', stopOpacity: 0.05 }} />
                </linearGradient>
              </defs>
              {/* Grid */}
              <line x1="40" y1="180" x2="380" y2="180" stroke="#f1f3f5" strokeWidth="1"/>
              <line x1="40" y1="140" x2="380" y2="140" stroke="#f1f3f5" strokeWidth="1"/>
              <line x1="40" y1="100" x2="380" y2="100" stroke="#f1f3f5" strokeWidth="1"/>
              <line x1="40" y1="60" x2="380" y2="60" stroke="#f1f3f5" strokeWidth="1"/>
              <line x1="40" y1="20" x2="380" y2="20" stroke="#f1f3f5" strokeWidth="1"/>
              {/* Dynamic chart paths */}
              {completionTrends && completionTrends.length > 0 && (() => {
                const xStep = 300 / (completionTrends.length - 1);
                const maxValue = Math.max(...completionTrends.flatMap(t => [t.completed, t.enrolled, t.target]));
                const yScale = 160 / maxValue;

                const createPath = (dataKey: 'completed' | 'enrolled' | 'target') => {
                  return completionTrends.map((t, i) => {
                    const x = 50 + i * xStep;
                    const y = 180 - (t[dataKey] * yScale);
                    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                  }).join(' ');
                };

                const completedPath = createPath('completed');
                const enrolledPath = createPath('enrolled');
                const targetPath = createPath('target');

                return (
                  <>
                    {/* Completed area */}
                    <path d={`${completedPath} L ${50 + (completionTrends.length - 1) * xStep} 180 L 50 180 Z`} fill="url(#completedGradient)"/>
                    <path d={completedPath} stroke="#10b981" strokeWidth="2" fill="none"/>
                    {/* Enrolled area */}
                    <path d={`${enrolledPath} L ${50 + (completionTrends.length - 1) * xStep} 180 L 50 180 Z`} fill="url(#enrolledGradient)"/>
                    <path d={enrolledPath} stroke="#3b82f6" strokeWidth="2" fill="none"/>
                    {/* Target line */}
                    <path d={targetPath} stroke="#f59e0b" strokeWidth="2" fill="none" strokeDasharray="5,5"/>
                  </>
                );
              })()}
              {/* X-axis labels */}
              {completionTrends?.map((trend, index) => {
                const xStep = 300 / (completionTrends.length - 1);
                const x = 50 + index * xStep;
                return (
                  <text key={trend.month} x={x} y="200" fontSize="11" fill="#9ca3af" textAnchor="middle">
                    {trend.month}
                  </text>
                );
              })}
            </svg>
          </div>
          <div className="trainer-chart-legend">
            <div className="trainer-legend-item">
              <div className="trainer-legend-dot green"></div>
              <span>Completed</span>
            </div>
            <div className="trainer-legend-item">
              <div className="trainer-legend-dot blue"></div>
              <span>Enrolled</span>
            </div>
            <div className="trainer-legend-item">
              <div className="trainer-legend-dot" style={{ background: '#f59e0b' }}></div>
              <span>Target</span>
            </div>
          </div>
        </div>

        <div className="trainer-chart-card">
          <div className="trainer-chart-header">
            <div className="trainer-chart-title">
              <h3>Certification Distribution</h3>
              <p>Types of certifications earned</p>
            </div>
            <button className="trainer-chart-expand">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 3 21 3 21 9"/>
                <polyline points="9 21 3 21 3 15"/>
                <line x1="21" y1="3" x2="14" y2="10"/>
                <line x1="3" y1="21" x2="10" y2="14"/>
              </svg>
            </button>
          </div>
          <div className="trainer-pie-chart">
            <svg className="trainer-pie-svg" viewBox="0 0 200 200">
              {certificationDistribution && (() => {
                let currentAngle = -90; // Start from top
                const radius = 80;
                const cx = 100;
                const cy = 100;

                return certificationDistribution.map((cert, index) => {
                  const angle = (cert.percentage / 100) * 360;
                  const startAngle = currentAngle * (Math.PI / 180);
                  const endAngle = (currentAngle + angle) * (Math.PI / 180);

                  const x1 = cx + radius * Math.cos(startAngle);
                  const y1 = cy + radius * Math.sin(startAngle);
                  const x2 = cx + radius * Math.cos(endAngle);
                  const y2 = cy + radius * Math.sin(endAngle);

                  const largeArc = angle > 180 ? 1 : 0;
                  const path = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;

                  // Calculate label position
                  const midAngle = currentAngle + angle / 2;
                  const labelRadius = radius * 0.6;
                  const labelX = cx + labelRadius * Math.cos(midAngle * (Math.PI / 180));
                  const labelY = cy + labelRadius * Math.sin(midAngle * (Math.PI / 180));

                  currentAngle += angle;

                  return (
                    <g key={cert.type}>
                      <path d={path} fill={cert.color} />
                      {cert.percentage >= 15 && (
                        <>
                          <text x={labelX} y={labelY - 5} fontSize="10" fill={index === 2 ? "white" : "#1f2937"} fontWeight="600" textAnchor="middle">
                            {cert.type}
                          </text>
                          <text x={labelX} y={labelY + 7} fontSize="10" fill={index === 2 ? "white" : "#1f2937"} fontWeight="600" textAnchor="middle">
                            {cert.percentage}%
                          </text>
                        </>
                      )}
                    </g>
                  );
                });
              })()}
            </svg>
          </div>
          <div className="trainer-pie-legend">
            {certificationDistribution?.map((cert) => (
              <div key={cert.type} className="trainer-pie-legend-item">
                <div className="trainer-pie-legend-dot" style={{ background: cert.color }}></div>
                <div className="trainer-pie-legend-text">
                  <span>{cert.type}</span>
                  <span className="trainer-pie-legend-percent">{cert.percentage}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="trainer-leaderboard-card">
        <div className="trainer-leaderboard-header">
          <div className="trainer-leaderboard-icon">
            <Award size={28} />
          </div>
          <div className="trainer-leaderboard-title">
            <h3>Leaderboard</h3>
            <p>Top Performing employees</p>
          </div>
        </div>

        <table className="trainer-leaderboard-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Employee Name</th>
              <th>Level</th>
              <th>Points</th>
              <th>Course Completed</th>
              <th>Certificates</th>
            </tr>
          </thead>
          <tbody>
            {leaderboardData?.map((emp) => (
              <tr key={emp.rank}>
                <td>
                  <div className="trainer-rank-cell">
                    {emp.rank === 1 && '🏆'}
                    {emp.rank === 2 && '🥈'}
                    {emp.rank === 3 && '🥉'}
                    {emp.rank > 3 && emp.rank}
                    {emp.trend === 'up' && <span className="trainer-trend-icon up">▲</span>}
                    {emp.trend === 'down' && <span className="trainer-trend-icon down">▼</span>}
                    {emp.trend === 'same' && <span className="trainer-trend-icon same">−</span>}
                  </div>
                </td>
                <td>
                  <div className="trainer-employee-cell">
                    <div className="trainer-employee-avatar">{emp.initials}</div>
                    <span>{emp.name}</span>
                  </div>
                </td>
                <td>
                  <div className="trainer-level-cell">
                    <span className="trainer-level-badge" style={{ background: emp.levelColor }}>
                      {emp.level}
                    </span>
                    <div className="trainer-level-progress">
                      <div className="trainer-progress-bar">
                        <div className="trainer-progress-fill" style={{ width: `${emp.progress}%`, background: emp.levelColor }}></div>
                      </div>
                      <div className="trainer-progress-text">{emp.progressText}</div>
                    </div>
                  </div>
                </td>
                <td><strong>{emp.points}</strong></td>
                <td>{emp.coursesCompleted}</td>
                <td>{emp.certificates}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="trainer-pagination">
          <div className="trainer-pagination-info">
            <span>Showing</span>
            <select className="trainer-pagination-select">
              <option>10</option>
              <option>25</option>
              <option>50</option>
            </select>
            <span>of 50</span>
          </div>
          <div className="trainer-pagination-buttons">
            <button className="trainer-page-btn">‹</button>
            <button className="trainer-page-btn active">1</button>
            <button className="trainer-page-btn">2</button>
            <button className="trainer-page-btn">3</button>
            <button className="trainer-page-btn">4</button>
            <button className="trainer-page-btn">5</button>
            <button className="trainer-page-btn">›</button>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="trainer-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="trainer-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="trainer-modal-left">
              <div className="trainer-modal-header">
                <div className="trainer-modal-icon">
                  <Users size={24} />
                </div>
                <div className="trainer-modal-title">
                  <h2>Generate Report</h2>
                  <p>Learning Engagement Report</p>
                </div>
                <button className="trainer-modal-close" onClick={() => setShowModal(false)}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>

              <div className="trainer-modal-section">
                <div className="trainer-section-header" onClick={() => setShowBasicConfig(!showBasicConfig)}>
                  <div className="trainer-section-header-left">
                    <div className="trainer-section-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                    </div>
                    <span className="trainer-section-title">Basic Configuration</span>
                  </div>
                  <svg className={`section-chevron ${showBasicConfig ? 'open' : ''}`} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </div>
                {showBasicConfig && (
                  <div className="trainer-section-content">
                    <div className="trainer-form-group">
                      <label className="trainer-form-label">From Date <span>*</span></label>
                      <input type="text" className="trainer-date-input" placeholder="dd-mm-yyyy" />
                    </div>
                    <div className="trainer-form-group">
                      <label className="trainer-form-label">To Date <span>*</span></label>
                      <input type="text" className="trainer-date-input" placeholder="dd-mm-yyyy" />
                    </div>
                    <div className="trainer-form-group">
                      <label className="trainer-form-label">Output Format(s) <span>*</span></label>
                      <div className="trainer-format-grid">
                        <div 
                          className={`format-card ${selectedFormats.includes('PDF') ? 'selected' : ''}`}
                          onClick={() => handleFormatToggle('PDF')}
                        >
                          <div className="trainer-format-card-icon">📄</div>
                          <div className="trainer-format-card-name">PDF</div>
                          <div className="trainer-format-card-check">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          </div>
                        </div>
                        <div 
                          className={`format-card ${selectedFormats.includes('EXCEL') ? 'selected' : ''}`}
                          onClick={() => handleFormatToggle('EXCEL')}
                        >
                          <div className="trainer-format-card-icon">📊</div>
                          <div className="trainer-format-card-name">EXCEL</div>
                          <div className="trainer-format-card-check">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="trainer-modal-section">
                <div className="trainer-section-header" onClick={() => setShowFiltersSegments(!showFiltersSegments)}>
                  <div className="trainer-section-header-left">
                    <div className="trainer-section-icon">
                      <Filter size={18} />
                    </div>
                    <span className="trainer-section-title">Filters & Segments</span>
                  </div>
                  <svg className={`section-chevron ${showFiltersSegments ? 'open' : ''}`} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </div>
              </div>

              <div className="trainer-modal-section">
                <div className="trainer-section-header" onClick={() => setShowReportSections(!showReportSections)}>
                  <div className="trainer-section-header-left">
                    <div className="trainer-section-icon">
                      <FileText size={18} />
                    </div>
                    <span className="trainer-section-title">Report Sections</span>
                  </div>
                  <svg className={`section-chevron ${showReportSections ? 'open' : ''}`} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </div>
              </div>

              <div className="trainer-modal-footer">
                <div className="trainer-footer-info">
                  5 sections • {selectedFormats.length} format(s)
                </div>
                <div className="trainer-modal-actions">
                  <button className="trainer-modal-btn cancel" onClick={() => setShowModal(false)}>Cancel</button>
                  <button className="trainer-modal-btn generate">
                    <Download size={18} />
                    Generate Now
                  </button>
                </div>
              </div>
            </div>

            <div className="trainer-modal-right">
              <div className="trainer-filter-section-header" onClick={() => setShowFiltersSegments(!showFiltersSegments)}>
                <Filter size={18} color="#f97316" />
                <span style={{ fontWeight: 600, fontSize: '14px' }}>Filters & Segments</span>
                <svg style={{ marginLeft: 'auto' }} className={`section-chevron ${showFiltersSegments ? 'open' : ''}`} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </div>

              {showFiltersSegments && (
                <>
                  <label className="trainer-form-label">Departments (Optional)</label>
                  <div className="trainer-departments-grid">
                    {['Engineering', 'Marketing', 'Sales', 'HR', 'Finance', 'Operations'].map(dept => (
                      <div 
                        key={dept}
                        className="trainer-checkbox-item"
                        onClick={() => handleDepartmentToggle(dept)}
                      >
                        <div className={`checkbox ${selectedDepartments.includes(dept) ? 'checked' : ''}`}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </div>
                        <span className="trainer-checkbox-label">{dept}</span>
                      </div>
                    ))}
                  </div>
                  <div className="trainer-helper-text">All departments will be included</div>
                </>
              )}

              <div className="trainer-container-section" style={{ marginTop: '24px' }}>
                <div className="trainer-container-header">
                  <div className="trainer-container-title">
                    <FileText size={18} color="#f97316" />
                    <span>Report Sections</span>
                  </div>
                  <svg className="trainer-collapse-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </div>
                <div className="trainer-section-items">
                  {selectedSections.map(section => (
                    <div key={section} className="trainer-section-item" onClick={() => handleSectionToggle(section)}>
                      <div className="trainer-section-checkbox">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </div>
                      <span className="trainer-section-item-label">{section}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="trainer-container-section">
                <div className="trainer-container-header">
                  <div className="trainer-container-title">
                    <Calendar size={18} color="#f97316" />
                    <span>Schedule & Distribution</span>
                  </div>
                  <svg className="trainer-collapse-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </div>
                <div className="trainer-schedule-item">
                  <div className="trainer-schedule-checkbox">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </div>
                  <div className="trainer-schedule-info">
                    <h4>Enable Automatic Scheduling</h4>
                    <p>Automatically generate and send this report</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsAnalytics;